import { openDB, type IDBPDatabase } from "idb";
import type { AppCurrency } from "../lib/localePrefs";
import { resolvePrice } from "../lib/price";
import type { CartMap, Pixel } from "../types";

// IndexedDB-backed cart store. Mirrors the schema used by the legacy
// js/cart.js (single object store "cart", keyPath "pixel").

interface CartSchema {
  cart: {
    key: string;
    value: Pixel & { pixel: number };
  };
}

let dbPromise: Promise<IDBPDatabase<CartSchema>> | null = null;

function getDB(): Promise<IDBPDatabase<CartSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<CartSchema>("CartDB", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("cart")) {
          db.createObjectStore("cart", { keyPath: "pixel" });
        }
      },
    });
  }
  return dbPromise;
}

export async function getCart(): Promise<CartMap> {
  const db = await getDB();
  const items = await db.getAll("cart");
  const out: CartMap = {};
  for (const p of items) {
    out[String(p.pixel)] = p as Pixel;
  }
  return out;
}

export async function putCartItem(p: Pixel): Promise<void> {
  const db = await getDB();
  await db.put("cart", { ...p, pixel: p.pixel });
}

export async function deleteCartItem(pixel: number | string): Promise<void> {
  const db = await getDB();
  const key = typeof pixel === "number" ? pixel : Number(pixel);
  await db.delete("cart", key);
}

export async function clearCart(): Promise<void> {
  const db = await getDB();
  await db.clear("cart");
}

export function calculateTotal(
  cart: CartMap,
  currency: AppCurrency,
): number {
  let total = 0;
  for (const key in cart) {
    const item = cart[key];
    total += resolvePrice(item.region?.properties?.price, currency);
  }
  return total;
}
